import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useMutation, useReactiveVar } from '@apollo/client'
import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import { Analytics } from 'shared-components'
import { PhoneInputMask } from 'shared-components'
import { boolean, date, object, string } from 'yup'

import {
  alertType,
  appointmentDateTime,
  availabilitySlotIdVar,
  message,
  patientData,
  resetAvailabilityCache,
  store,
} from '../../cache'
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '../../constants'
import { BOOK_APPOINTMENT } from '../../queries/bookAppointment'

import './style.css'

export const BookingForm = () => {
  const referrer = window.document.referrer
  const { locationId, partnerId } = useReactiveVar(store)
  const availabilitySlotId = useReactiveVar(availabilitySlotIdVar)
  const datetime = useReactiveVar(appointmentDateTime)
  const patient = useReactiveVar(patientData)
  const [isBackdropOpen, setIsBackdropOpen] = useState(false)
  const [submitAppointment, { loading }] = useMutation(BOOK_APPOINTMENT)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const termsOfUseLabel = { inputProps: { 'aria-label': 'Terms of Use' } }

  // Define min and max birthdates for dob field validation
  const maxDob = new Date()
  maxDob.setFullYear(maxDob.getFullYear() - 1)
  const minDob = new Date()
  minDob.setFullYear(minDob.getFullYear() - 120)
  const minDobFormatted = format(minDob, 'MM/dd/yyyy')
  const maxDobFormatted = format(maxDob, 'MM/dd/yyyy')
  const dobValidationMsg = t(
    'Date of birth must be between {{minDobFormatted}} and {{maxDobFormatted}}',
    {
      minDobFormatted,
      maxDobFormatted,
      interpolation: { escapeValue: false },
    }
  )

  // Define allowed characters for name field validation
  const validNameFormat = /^[\p{L}\s-]+$/u
  const nameValidationMsg = t('Name may only contain letters and hyphens')

  // Define format for email field validation
  const validEmailFormat =
    /^(?:(?:[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+(?:\.[\p{L}\p{N}!#$%&'*+/=?^_`{|}~-]+)*)|(?:"(?:[\p{L}\p{N}\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\p{L}\p{N}\x01-\x09\x0b\x0c\x0e-\x7f])*"))@(?:(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+[\p{L}]{2,}|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}|IPv6:(?:[a-fA-F0-9]{1,4}(?::[a-fA-F0-9]{1,4}){7}))\])$/u
  const emailValidationMsg = t(
    'Email must be formatted as username@domain.com and cannot contain special characters in the domain'
  )

  const validationSchema = object({
    firstName: string()
      .max(250)
      .matches(validNameFormat, nameValidationMsg)
      .required(t('First name required')),
    lastName: string()
      .max(250)
      .matches(validNameFormat, nameValidationMsg)
      .required(t('Last name required')),
    dob: date()
      .max(maxDob, dobValidationMsg)
      .min(minDob, dobValidationMsg)
      .required(t('Date of birth required')),
    email: string()
      .max(320)
      .matches(validEmailFormat, emailValidationMsg)
      .required(t('Email required')),
    phone: string()
      .test(
        'is-valid-phone',
        t('Phone number should contain 10 digits'),
        value => {
          const digitsOnly = value ? value.replace(/\D/g, '') : ''
          return digitsOnly.length === 10
        }
      )
      .required(t('Mobile phone required')),
    comment: string().max(500),
    termsOfUse: boolean().oneOf([true]).required(),
  })

  const formik = useFormik({
    initialValues: {
      firstName: (patient && patient.firstName) || '',
      lastName: (patient && patient.lastName) || '',
      email: (patient && patient.email) || '',
      phone: (patient && patient.phone) || '',
      dob: (patient && patient.dob) || '',
      comment: (patient && patient.comment) || '',
      termsOfUse: false,
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      setIsBackdropOpen(true)
      const { termsOfUse, ...patientValues } = values
      patientData(patientValues)
      const variables = {
        availabilitySlotId,
        date: format(datetime, 'yyyy-MM-dd'),
        patient: patientValues,
        partnerId,
        referrer: referrer,
      }
      try {
        const { data } = await submitAppointment({ variables })
        switch (data.bookPmsAppointment.__typename) {
          case 'AppointmentTimeInvalid':
            await resetAvailabilityCache()
            alertType('warning')
            message(
              t(
                'Oops! That appointment time is no longer available. Please choose a new date or time while we keep your form saved.'
              )
            )
            navigate('/')
            break
          case 'AppointmentUnavailable':
            await resetAvailabilityCache()
            alertType('warning')
            message(
              t(
                'Oops! That appointment time is no longer available. Please choose a new date or time while we keep your form saved.'
              )
            )
            navigate('/')
            break
          case 'AppointmentLimitReached':
            await resetAvailabilityCache()
            alertType('error')
            message(
              t(
                "It looks like you've booked an appointment within the last 24 hours. We recommend contacting the practice directly to make any changes. If you believe this may be a mistake, please email support@intelibly.com."
              )
            )
            navigate('/')
            break
          default:
            if (data.bookPmsAppointment.id && data.bookPmsAppointment.start) {
              navigate('/booking-confirmation')
            }
        }
      } catch (error) {
        alertType('error')
        message(
          t(
            "Oops! Something didn't quite go as planned. Give it another try later and we'll get things sorted out."
          )
        )
        navigate('/')
      } finally {
        setIsBackdropOpen(false)
      }
    },
  })
  const handleBlur = event => {
    Analytics.bookingFormFieldFilled({
      fieldName: event.target.name,
      locationId,
      partnerId,
    })
    formik.handleBlur(event)
    patientData(formik.values)
  }
  return (
    <form className="form" onSubmit={formik.handleSubmit}>
      <TextField
        id="first-name"
        value={formik.values.firstName}
        name="firstName"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        label={t('First Name')}
        variant="outlined"
        disabled={formik.isSubmitting}
        InputProps={{
          inputProps: { maxLength: 50 },
        }}
        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
        helperText={formik.touched.firstName && formik.errors.firstName}
        required
        fullWidth
      />
      <TextField
        id="last-name"
        value={formik.values.lastName}
        name="lastName"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
        helperText={formik.touched.lastName && formik.errors.lastName}
        disabled={formik.isSubmitting}
        label={t('Last Name')}
        variant="outlined"
        required
        fullWidth
      />
      <TextField
        id="date"
        value={formik.values.dob}
        name="dob"
        type="date"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        label={t('Date of Birth')}
        variant="outlined"
        error={formik.touched.dob && Boolean(formik.errors.dob)}
        helperText={formik.touched.dob && formik.errors.dob}
        InputLabelProps={{ shrink: true }}
        disabled={formik.isSubmitting}
        required
        fullWidth
      />
      <TextField
        id="email"
        value={formik.values.email}
        name="email"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
        disabled={formik.isSubmitting}
        label="Email"
        variant="outlined"
        required
        fullWidth
      />
      <TextField
        id="phone"
        type="tel"
        name="phone"
        variant="outlined"
        label={t('Mobile Phone')}
        value={formik.values.phone}
        onChange={formik.handleChange}
        onBlur={handleBlur}
        error={formik.touched.phone && Boolean(formik.errors.phone)}
        helperText={formik.touched.phone && formik.errors.phone}
        disabled={formik.isSubmitting}
        InputProps={{ inputComponent: PhoneInputMask }}
        required
        fullWidth
      />
      <TextField
        id="comment"
        value={formik.values.comment}
        name="comment"
        onChange={formik.handleChange}
        onBlur={handleBlur}
        label={t('Comment')}
        variant="outlined"
        minRows={2}
        maxRows={4}
        fullWidth
        multiline
        error={formik.touched.comment && Boolean(formik.errors.comment)}
        disabled={formik.isSubmitting}
        helperText={
          formik.errors.comment
            ? formik.errors.comment
            : t(`Do not include any protected health information in the comment
        field. Additional details can be provided when contacted by the practice
        directly.`)
        }
        InputProps={{
          inputProps: { maxLength: 500 },
          sx: [
            {
              textarea: { marginBottom: theme => theme.spacing(2) },
            },
          ],
          endAdornment: (
            <InputAdornment
              disableTypography
              disablePointerEvents
              position="start"
              sx={[{ alignSelf: 'flex-end', position: 'absolute' }]}
            >
              {formik.values.comment.length} {t('characters')}
            </InputAdornment>
          ),
        }}
      />
      <Grid
        container
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={1.5}
      >
        <Grid
          container
          item
          display="flex"
          flexDirection="row"
          alignItems="center"
          wrap="nowrap"
          gap={1}
        >
          <Checkbox
            {...termsOfUseLabel}
            name="termsOfUse"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            color="primary"
            required
          />
          <Typography variant="caption" color="rgba(0, 0, 0, 0.6)">
            I have read and accept Intelibly's{' '}
            <Link
              data-testid="form-terms-and-conditions"
              variant="caption"
              href={TERMS_AND_CONDITIONS_URL}
              target="_blank"
              rel="noopener"
            >
              Terms and Conditions
            </Link>{' '}
            and I consent to Intelibly collecting data, including sensitive
            information as fully described in the{' '}
            <Link
              data-testid="form-privacy-policy"
              variant="caption"
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener"
            >
              Privacy Policy
            </Link>
            .
          </Typography>
        </Grid>
        <Grid item>
          <Button
            aria-label="confirm"
            color="primary"
            disabled={formik.isSubmitting}
            disableElevation
            fullWidth
            type="submit"
            variant="contained"
          >
            {t('Confirm')}
          </Button>
        </Grid>
      </Grid>
      <Backdrop
        open={isBackdropOpen || loading}
        sx={{
          color: '#fff',
          opacity: 2,
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </form>
  )
}
